import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  playLeverPullSound, 
  playReelSpinSound, 
  playReelStopSound, 
  playJackpotSound, 
  playLoseSound 
} from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { 
  FiFrown, 
  FiTag, 
  FiDollarSign, 
  FiGift, 
  FiAward, 
  FiZap, 
  FiVolume2, 
  FiVolumeX, 
  FiList, 
  FiCheckCircle, 
  FiX, 
  FiStar,
  FiPercent,
  FiArrowRight
} = FiIcons;

const REEL_ITEM_HEIGHT = 64; // Compact 64px per item
const VIEWPORT_HEIGHT = 192; // Exactly 3 visible items (64 * 3 = 192px)

// Casino-grade symbol styling mapped dynamically to prizes
const getPrizeStyle = (prizeName) => {
  const name = (prizeName || '').toLowerCase();
  
  // Losses / Near-misses
  if (name.includes('try') || name.includes('luck') || name.includes('miss') || name.includes('again')) {
    return { 
      icon: FiFrown, 
      symbolLabel: 'TRY AGAIN',
      textColor: 'text-slate-400 dark:text-slate-400', 
      bg: 'bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900',
      border: 'border-slate-300 dark:border-slate-700',
      badgeBg: 'bg-slate-200/90 dark:bg-slate-700/90 text-slate-500',
      isLoss: true
    };
  }

  // Discounts & Coupons
  if (name.includes('discount') || name.includes('%') || name.includes('off') || name.includes('coupon')) {
    return { 
      icon: FiPercent, 
      symbolLabel: 'DISCOUNT',
      textColor: 'text-rose-600 dark:text-rose-400', 
      bg: 'bg-gradient-to-b from-rose-50 to-rose-100 dark:from-rose-950/40 dark:to-slate-900',
      border: 'border-rose-300 dark:border-rose-700/60',
      badgeBg: 'bg-rose-500 text-white shadow-sm',
      isLoss: false
    };
  }

  // Cash, Cashback, Currencies
  if (name.includes('cashback') || name.includes('cash') || name.includes('₹') || name.includes('$') || name.includes('money')) {
    return { 
      icon: FiDollarSign, 
      symbolLabel: 'CASH',
      textColor: 'text-emerald-600 dark:text-emerald-400', 
      bg: 'bg-gradient-to-b from-emerald-50 to-emerald-100 dark:from-emerald-950/40 dark:to-slate-900',
      border: 'border-emerald-300 dark:border-emerald-700/60',
      badgeBg: 'bg-emerald-500 text-white shadow-sm',
      isLoss: false
    };
  }

  // Free Gifts, Samples, Swag
  if (name.includes('gift') || name.includes('free') || name.includes('sample') || name.includes('hamper')) {
    return { 
      icon: FiGift, 
      symbolLabel: 'GIFT',
      textColor: 'text-purple-600 dark:text-purple-400', 
      bg: 'bg-gradient-to-b from-purple-50 to-purple-100 dark:from-purple-950/40 dark:to-slate-900',
      border: 'border-purple-300 dark:border-purple-700/60',
      badgeBg: 'bg-purple-500 text-white shadow-sm',
      isLoss: false
    };
  }

  // Top Tier / Bumper / Grand / Default Trophy
  return { 
    icon: FiStar, 
    symbolLabel: 'JACKPOT',
    textColor: 'text-amber-500 dark:text-amber-400', 
    bg: 'bg-gradient-to-b from-amber-50 to-amber-100 dark:from-amber-950/40 dark:to-slate-900',
    border: 'border-amber-300 dark:border-amber-700/60',
    badgeBg: 'bg-amber-500 text-slate-950 font-black shadow-sm',
    isLoss: false
  };
};

const Reel = ({ 
  reelIndex, 
  items, 
  isSpinning, 
  currentY, 
  targetIndex, 
  isWinnerLocked 
}) => {
  return (
    <div className="flex-1 h-[192px] overflow-hidden bg-slate-950 border-x border-amber-900/60 relative select-none">
      {/* Curved 3D Cylinder Shadows: Dark top and bottom illusion */}
      <div className="absolute inset-0 z-20 pointer-events-none shadow-[inset_0_32px_24px_-8px_rgba(0,0,0,0.92),inset_0_-32px_24px_-8px_rgba(0,0,0,0.92)]"></div>
      
      {/* Chrome Glass Reflection Streak */}
      <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-b from-white/10 via-transparent to-black/30"></div>

      {/* Target Payline Glow on Winning Stop */}
      {isWinnerLocked && (
        <div className="absolute top-[64px] left-0 right-0 h-[64px] z-15 pointer-events-none border-y-2 border-amber-400 bg-amber-400/20 animate-pulse shadow-[0_0_15px_rgba(251,191,36,0.5)]"></div>
      )}

      <motion.div
        className="flex flex-col w-full absolute top-0"
        initial={{ y: 0 }}
        animate={{ y: currentY }}
        transition={{ 
          duration: reelIndex === 0 ? 1.3 : reelIndex === 1 ? 1.7 : 2.1,
          ease: isSpinning ? [0.2, 0.9, 0.3, 1] : [0.15, 0.85, 0.25, 1.05] 
        }}
        style={{
          filter: isSpinning ? 'blur(1.2px)' : 'none'
        }}
      >
        {items.map((item, i) => {
          const style = getPrizeStyle(item);
          const isCenter = i === targetIndex;
          return (
            <div 
              key={i} 
              className={`h-[64px] flex flex-col items-center justify-center border-b border-slate-800/80 px-1 text-center transition-all relative overflow-hidden ${style.bg} ${isCenter && isWinnerLocked ? 'ring-2 ring-inset ring-amber-400/90' : ''}`}
            >
              <div className={`w-6 h-6 rounded-full shadow-sm border ${style.border} ${style.badgeBg} flex items-center justify-center mb-0.5 shrink-0`}>
                <SafeIcon icon={style.icon} className="text-xs" />
              </div>
              <span className={`line-clamp-2 text-[10px] sm:text-[11px] font-black tracking-tight leading-tight px-0.5 ${style.textColor}`}>
                {item}
              </span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

const SlotMachine = ({ 
  prizes = [], 
  onFinished, 
  targetPrize = "",
  compact = false
}) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [leverPulled, setLeverPulled] = useState(false);
  const [reels, setReels] = useState([[], [], []]);
  const [targetIndices, setTargetIndices] = useState([0, 0, 0]);
  const [reelPositions, setReelPositions] = useState([0, 0, 0]);
  const [showPaytable, setShowPaytable] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activePrizeWon, setActivePrizeWon] = useState(targetPrize);
  const cancelSpinAudio = useRef(null);

  // Initialize sound settings
  useEffect(() => {
    const s = getSettings();
    setSoundEnabled(s.soundEnabled !== false);
  }, []);

  // Determine safe Dynamic Pool
  const safePrizes = prizes && prizes.length > 0 
    ? prizes 
    : ["₹50 Discount", "Free Premium Gift", "Better Luck Next Time", "Free Product Sample", "10% Cashback"];

  // Effective target prize
  const effectiveTarget = activePrizeWon || targetPrize || safePrizes[0];
  const isWin = !effectiveTarget.toLowerCase().includes('try') && 
                !effectiveTarget.toLowerCase().includes('luck') && 
                !effectiveTarget.toLowerCase().includes('miss');

  // Build reels layout
  useEffect(() => {
    const numItems = 45;

    const generateReel = (targetSymbol) => {
      const items = [];
      for (let i = 0; i < numItems; i++) {
        items.push(safePrizes[Math.floor(Math.random() * safePrizes.length)]);
      }
      const tIndex = numItems - 5;
      items[tIndex] = targetSymbol;
      return { items, tIndex };
    };

    const r1 = generateReel(effectiveTarget);
    const r2 = generateReel(effectiveTarget);

    let finalSymbolR3 = effectiveTarget;
    if (!isWin) {
      // Near-miss tension: Reel 1 & 2 match, Reel 3 stops on alternative symbol
      const altPrizes = safePrizes.filter(p => p !== effectiveTarget);
      finalSymbolR3 = altPrizes.length > 0 
        ? altPrizes[Math.floor(Math.random() * altPrizes.length)] 
        : "Better Luck";
    }
    const r3 = generateReel(finalSymbolR3);

    setReels([r1.items, r2.items, r3.items]);
    setTargetIndices([r1.tIndex, r2.tIndex, r3.tIndex]);
    
    // Initial resting positions
    if (!hasSpun) {
      setReelPositions([0, 0, 0]);
    }
  }, [effectiveTarget, safePrizes.length, isWin]);

  const handleSpin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setLeverPulled(true);
    setHasSpun(false);

    // Pick target prize
    const chosenPrize = targetPrize || safePrizes[Math.floor(Math.random() * safePrizes.length)];
    setActivePrizeWon(chosenPrize);

    // Audio effects
    if (soundEnabled) {
      playLeverPullSound();
      cancelSpinAudio.current = playReelSpinSound(2300);

      // Distinct Reel Stop clunks
      setTimeout(() => playReelStopSound(1), 1300);
      setTimeout(() => playReelStopSound(2), 1700);
      setTimeout(() => playReelStopSound(3), 2100);
    }

    // Release lever
    setTimeout(() => {
      setLeverPulled(false);
    }, 350);

    // Center viewport row is row index 1 (at REEL_ITEM_HEIGHT = 64px)
    const finalY1 = -(targetIndices[0] * REEL_ITEM_HEIGHT) + REEL_ITEM_HEIGHT;
    const finalY2 = -(targetIndices[1] * REEL_ITEM_HEIGHT) + REEL_ITEM_HEIGHT;
    const finalY3 = -(targetIndices[2] * REEL_ITEM_HEIGHT) + REEL_ITEM_HEIGHT;

    // Trigger reel animations
    setReelPositions([finalY1, finalY2, finalY3]);

    // Conclude after 3rd reel locks completely
    setTimeout(() => {
      setIsSpinning(false);
      setHasSpun(true);
      if (cancelSpinAudio.current) cancelSpinAudio.current();

      const wonIsGood = !chosenPrize.toLowerCase().includes('try') && 
                        !chosenPrize.toLowerCase().includes('luck') && 
                        !chosenPrize.toLowerCase().includes('miss');

      if (soundEnabled) {
        if (wonIsGood) {
          playJackpotSound();
        } else {
          playLoseSound();
        }
      }

      if (onFinished) {
        onFinished(chosenPrize);
      }
    }, 2350);
  };

  return (
    <div className={`relative w-full ${compact ? 'max-w-sm' : 'max-w-md'} mx-auto flex flex-col items-center justify-center select-none py-1 px-1`}>
      
      {/* Vegas Arcade Slot Cabinet */}
      <div className="relative w-full bg-gradient-to-b from-red-600 via-red-800 to-amber-950 p-3 sm:p-4 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.65),0_0_35px_rgba(239,68,68,0.3)] border-[5px] border-amber-400">
        
        {/* Golden Corner Bolts */}
        <div className="absolute top-2 left-2 w-3 h-3 rounded-full bg-gradient-to-br from-yellow-100 via-amber-400 to-amber-600 shadow-sm border border-yellow-200"></div>
        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-gradient-to-br from-yellow-100 via-amber-400 to-amber-600 shadow-sm border border-yellow-200"></div>
        <div className="absolute bottom-2 left-2 w-3 h-3 rounded-full bg-gradient-to-br from-yellow-100 via-amber-400 to-amber-600 shadow-sm border border-yellow-200"></div>
        <div className="absolute bottom-2 right-2 w-3 h-3 rounded-full bg-gradient-to-br from-yellow-100 via-amber-400 to-amber-600 shadow-sm border border-yellow-200"></div>

        {/* Header Marquee & Lights */}
        <div className="mb-2.5 text-center relative">
          
          {/* Quick Sound & Paytable Buttons */}
          <div className="absolute right-0 top-0 flex items-center gap-1 z-30">
            <button 
              type="button"
              onClick={() => setShowPaytable(!showPaytable)}
              className="w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 border border-amber-400/40 text-amber-300 flex items-center justify-center text-[10px] transition-colors"
              title="View Dynamic Reward Pool & Paytable"
            >
              <SafeIcon icon={FiList} />
            </button>
            <button 
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 border border-amber-400/40 text-amber-300 flex items-center justify-center text-[10px] transition-colors"
              title={soundEnabled ? "Mute Slot Sounds" : "Unmute Slot Sounds"}
            >
              <SafeIcon icon={soundEnabled ? FiVolume2 : FiVolumeX} />
            </button>
          </div>

          {/* Dancing LED Chase Lights */}
          <div className="flex justify-center items-center gap-1.5 mb-1.5 px-6">
            {[...Array(9)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full transition-all duration-300 shadow-sm ${
                  isSpinning 
                    ? (i % 2 === 0 ? 'bg-amber-300 shadow-[0_0_8px_#fde047]' : 'bg-red-400 shadow-[0_0_8px_#f87171]')
                    : hasSpun && isWin
                    ? 'bg-amber-300 animate-ping shadow-[0_0_10px_#fde047]'
                    : 'bg-amber-400 shadow-[0_0_5px_#fde047]'
                }`}
                style={{ 
                  animation: isSpinning 
                    ? `pulse 0.35s infinite alternate ${i * 0.08}s` 
                    : 'none' 
                }}
              />
            ))}
          </div>

          {/* Golden Casino Title Crown */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-400 text-red-950 font-black text-xs tracking-wider uppercase shadow-md border border-amber-300">
            <SafeIcon icon={FiZap} className="text-red-700 animate-bounce text-xs" />
            <span>LUCKY CASINO SLOTS</span>
            <SafeIcon icon={FiZap} className="text-red-700 animate-bounce text-xs" />
          </div>
        </div>

        {/* 3D Reel Housing Chamber (Height: 192px) */}
        <div className="bg-slate-950 rounded-2xl p-2 mb-3 border-4 border-amber-500/90 shadow-[inset_0_10px_20px_rgba(0,0,0,0.9),0_4px_15px_rgba(0,0,0,0.5)] relative overflow-hidden">
          
          {/* Glass Visor Highlight */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-transparent pointer-events-none z-30"></div>

          {/* Winning Center Payline Frame (Center Row: 64px to 128px) */}
          <div className="absolute top-[64px] left-0 right-0 h-[64px] z-25 pointer-events-none flex items-center justify-between px-0.5 border-y-2 border-amber-400/80 bg-amber-400/5">
            <div className={`px-1 py-0.5 rounded-r-md text-[8px] font-black uppercase tracking-wider flex items-center gap-0.5 shadow-md ${
              hasSpun && isWin 
                ? 'bg-amber-400 text-slate-950 animate-bounce' 
                : 'bg-red-600 text-white'
            }`}>
              PAYLINE ▶
            </div>
            
            <div className={`h-0.5 flex-1 mx-1 ${
              hasSpun && isWin
                ? 'bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-400 shadow-[0_0_15px_#f59e0b]'
                : isSpinning
                ? 'bg-gradient-to-r from-red-500 via-amber-300 to-red-500 animate-pulse'
                : 'bg-red-500/50'
            }`}></div>

            <div className={`px-1 py-0.5 rounded-l-md text-[8px] font-black uppercase tracking-wider flex items-center gap-0.5 shadow-md ${
              hasSpun && isWin 
                ? 'bg-amber-400 text-slate-950 animate-bounce' 
                : 'bg-red-600 text-white'
            }`}>
              ◀ PAYLINE
            </div>
          </div>

          {/* 3 Reel Columns */}
          <div className="flex justify-between h-[192px] bg-slate-900 rounded-xl overflow-hidden relative border border-slate-800">
            {reels.map((reelItems, idx) => (
              <Reel 
                key={idx}
                reelIndex={idx}
                items={reelItems}
                isSpinning={isSpinning}
                currentY={reelPositions[idx]}
                targetIndex={targetIndices[idx]}
                isWinnerLocked={hasSpun && isWin}
              />
            ))}
          </div>

          {/* Win / Result Ribbon Overlay on Finished Spin */}
          <AnimatePresence>
            {hasSpun && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className={`mt-2 py-1 px-2.5 rounded-xl text-center text-[11px] font-black uppercase tracking-wider border shadow-md flex items-center justify-center gap-1.5 ${
                  isWin 
                    ? 'bg-gradient-to-r from-amber-500 via-yellow-300 to-amber-500 text-slate-950 border-amber-300 animate-pulse'
                    : 'bg-slate-800 text-slate-300 border-slate-700'
                }`}
              >
                <SafeIcon icon={isWin ? FiStar : FiFrown} className="text-xs" />
                <span className="truncate">{isWin ? `★ 3x MATCH JACKPOT: ${effectiveTarget} ★` : `RESULT: ${effectiveTarget}`}</span>
                <SafeIcon icon={isWin ? FiStar : FiFrown} className="text-xs" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Ergonomic 3D Arcade Control Deck */}
        <div className="flex flex-col items-center justify-center">
          <button
            type="button"
            onClick={handleSpin}
            disabled={isSpinning}
            className="group relative w-full max-w-[220px] h-13 sm:h-14 rounded-2xl bg-gradient-to-b from-yellow-300 via-amber-400 to-yellow-600 border-4 border-amber-600 shadow-[0_6px_0_#78350f,0_10px_20px_rgba(0,0,0,0.6)] active:shadow-[0_1px_0_#78350f,0_4px_10px_rgba(0,0,0,0.6)] active:translate-y-1.5 transition-all flex items-center justify-center gap-2 text-red-950 font-black text-lg sm:text-xl tracking-wider uppercase disabled:opacity-85 disabled:cursor-not-allowed"
          >
            {/* Bevel Reflection */}
            <div className="absolute inset-1 rounded-xl border border-white/60 pointer-events-none bg-gradient-to-b from-white/30 to-transparent"></div>
            
            <SafeIcon icon={FiZap} className="text-red-800 group-hover:scale-110 transition-transform" />
            <span className="drop-shadow-sm">
              {isSpinning ? "ROLLING..." : hasSpun ? "SPIN AGAIN" : "SPIN"}
            </span>
          </button>
        </div>

      </div>

      {/* Flush Mechanical Pull Lever (Anchored right at cabinet frame) */}
      <div 
        className="hidden md:flex flex-col items-center absolute -right-8 top-20 z-20 cursor-pointer" 
        onClick={handleSpin}
        title="Pull Slot Machine Lever"
      >
        {/* Mounting Bracket attached flush */}
        <div className="w-5 h-10 bg-gradient-to-r from-amber-700 via-amber-600 to-amber-900 rounded-r-md border border-amber-400 shadow-md"></div>
        
        {/* Lever Shaft */}
        <motion.div 
          className="w-2.5 bg-gradient-to-r from-slate-200 via-slate-400 to-slate-200 rounded-full shadow-lg origin-bottom -mt-2"
          animate={{ 
            height: leverPulled ? 35 : 75, 
            rotate: leverPulled ? 25 : 0 
          }}
          transition={{ duration: 0.22 }}
        >
          {/* Lever Knob Ball */}
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-400 via-red-600 to-red-900 border-2 border-amber-300 shadow-[0_4px_8px_rgba(0,0,0,0.6)] -ml-2.5 -mt-3 flex items-center justify-center hover:scale-110 transition-transform">
            <div className="w-2 h-2 rounded-full bg-white/70 -mt-0.5 -ml-0.5"></div>
          </div>
        </motion.div>
      </div>

      {/* Dynamic Reward Pool / Paytable Drawer Modal */}
      <AnimatePresence>
        {showPaytable && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md"
            onClick={() => setShowPaytable(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-amber-500/40 rounded-3xl p-5 max-w-sm w-full shadow-2xl text-left"
            >
              <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-sm">
                    <SafeIcon icon={FiList} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Dynamic Reward Pool</h3>
                    <p className="text-[10px] text-slate-400">Prizes active in this Slot Machine session</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowPaytable(false)}
                  className="w-6 h-6 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 flex items-center justify-center transition-colors text-xs"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
                {safePrizes.map((p, idx) => {
                  const style = getPrizeStyle(p);
                  const pct = ((1 / safePrizes.length) * 100).toFixed(0);
                  return (
                    <div 
                      key={idx} 
                      className={`p-2 rounded-xl border flex items-center justify-between gap-2 ${
                        style.isLoss 
                          ? 'bg-slate-800/40 border-slate-700/60 text-slate-400' 
                          : 'bg-slate-800/70 border-slate-700 text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs ${style.badgeBg}`}>
                          <SafeIcon icon={style.icon} />
                        </div>
                        <div>
                          <div className="text-xs font-bold leading-tight">{p}</div>
                          <div className="text-[9px] text-slate-400 uppercase font-mono">{style.symbolLabel} &bull; 3x MATCH</div>
                        </div>
                      </div>
                      <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-black/40 text-amber-400 border border-amber-400/20">
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-800 flex justify-end">
                <button 
                  type="button"
                  onClick={() => setShowPaytable(false)}
                  className="px-3.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default SlotMachine;